import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { ComparisonPanel } from '../components/measurements/ComparisonPanel';
import { AdvancedCharts } from '../components/measurements/AdvancedCharts';
import { DetailedMeasurementsTable } from '../components/measurements/DetailedMeasurementsTable';
import { exportMeasurementsToPDF } from '../utils/pdfExport';
import { sendMeasurementsPDFToPatient, sendPDFToPatient, generatePDFBlob } from '../services/pdfSendService';
import { PDFSendModal } from '../components/pdf/PDFSendModal';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../config/backend';
import { ref, get as rtdbGet, push, set, child } from '../services/phpDataAdapter';
import './Pages.css';

interface Measurement {
  id: string;
  timestamp: string;
  weight?: number;
  height?: number;
  bodyFatPercentage?: number;
  muscleMass?: number;
  waistCircumference?: number;
  hipCircumference?: number;
  armCircumference?: number;
  thighCircumference?: number;
  tricepsSkinfold?: number;
  bicepsSkinfold?: number;
  subscapularSkinfold?: number;
  suprailiacSkinfold?: number;
  abdominalSkinfold?: number;
  thighSkinfold?: number;
  calfSkinfold?: number;
  notes?: string;
}

export const Measurements: React.FC = () => {
  const { user } = useAuth();
  const { pacientes } = usePacientes();
  const [searchParams, setSearchParams] = useSearchParams();
  const patientId = searchParams.get('patientId');

  const [selectedPatient, setSelectedPatient] = useState<string>(patientId || '');
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPDFSendModalOpen, setIsPDFSendModalOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  const [newMeasurement, setNewMeasurement] = useState({
    weight: '',
    height: '',
    bodyFatPercentage: '',
    muscleMass: '',
    waistCircumference: '',
    hipCircumference: '',
    armCircumference: '',
    thighCircumference: '',
    tricepsSkinfold: '',
    bicepsSkinfold: '',
    subscapularSkinfold: '',
    suprailiacSkinfold: '',
    abdominalSkinfold: '',
    thighSkinfold: '',
    calfSkinfold: '',
    notes: '',
  });

  useEffect(() => {
    if (selectedPatient) {
      loadMeasurements(selectedPatient);
      setSearchParams({ patientId: selectedPatient });
    }
  }, [selectedPatient]);

  const loadMeasurements = async (pid: string) => {
    if (!pid) return;
    setLoading(true);
    try {
      const snap = await rtdbGet(child(ref(database), `users/${pid}/measurements`));
      if (snap.exists()) {
        const data = snap.val() as Record<string, any>;
        const list: Measurement[] = Object.entries(data)
          .map(([id, m]) => ({
            id,
            timestamp: m.timestamp || m.createdAt || new Date().toISOString(),
            weight: m.weight || null,
            height: m.height || null,
            bodyFatPercentage: m.bodyFatPercentage || null,
            muscleMass: m.muscleMass || null,
            waistCircumference: m.waistCircumference || null,
            hipCircumference: m.hipCircumference || null,
            armCircumference: m.armCircumference || null,
            thighCircumference: m.thighCircumference || null,
            tricepsSkinfold: m.tricepsSkinfold || null,
            bicepsSkinfold: m.bicepsSkinfold || null,
            subscapularSkinfold: m.subscapularSkinfold || null,
            suprailiacSkinfold: m.suprailiacSkinfold || null,
            abdominalSkinfold: m.abdominalSkinfold || null,
            thighSkinfold: m.thighSkinfold || null,
            calfSkinfold: m.calfSkinfold || null,
            notes: m.notes || '',
          }))
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        setMeasurements(list);
      } else {
        setMeasurements([]);
      }
    } catch (error) {
      console.error('Erro ao carregar medições:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedPatient || !user) return;

    setSaving(true);
    try {
      // Salvar timestamp em múltiplos formatos para garantir compatibilidade
      const now = Date.now();
      const measurementData: any = {
        timestamp: now, // Número (milissegundos) - formato principal
        date: now, // Backup em campo alternativo
        createdAt: now, // Backup em campo alternativo
        timestampISO: new Date().toISOString(), // String ISO para compatibilidade
        nutritionistId: user.uid,
      };

      if (newMeasurement.weight) measurementData.weight = parseFloat(newMeasurement.weight);
      if (newMeasurement.height) measurementData.height = parseFloat(newMeasurement.height);
      if (newMeasurement.bodyFatPercentage) measurementData.bodyFatPercentage = parseFloat(newMeasurement.bodyFatPercentage);
      if (newMeasurement.muscleMass) measurementData.muscleMass = parseFloat(newMeasurement.muscleMass);
      if (newMeasurement.waistCircumference) measurementData.waistCircumference = parseFloat(newMeasurement.waistCircumference);
      if (newMeasurement.hipCircumference) measurementData.hipCircumference = parseFloat(newMeasurement.hipCircumference);
      if (newMeasurement.armCircumference) measurementData.armCircumference = parseFloat(newMeasurement.armCircumference);
      if (newMeasurement.thighCircumference) measurementData.thighCircumference = parseFloat(newMeasurement.thighCircumference);
      if (newMeasurement.tricepsSkinfold) measurementData.tricepsSkinfold = parseFloat(newMeasurement.tricepsSkinfold);
      if (newMeasurement.bicepsSkinfold) measurementData.bicepsSkinfold = parseFloat(newMeasurement.bicepsSkinfold);
      if (newMeasurement.subscapularSkinfold) measurementData.subscapularSkinfold = parseFloat(newMeasurement.subscapularSkinfold);
      if (newMeasurement.suprailiacSkinfold) measurementData.suprailiacSkinfold = parseFloat(newMeasurement.suprailiacSkinfold);
      if (newMeasurement.abdominalSkinfold) measurementData.abdominalSkinfold = parseFloat(newMeasurement.abdominalSkinfold);
      if (newMeasurement.thighSkinfold) measurementData.thighSkinfold = parseFloat(newMeasurement.thighSkinfold);
      if (newMeasurement.calfSkinfold) measurementData.calfSkinfold = parseFloat(newMeasurement.calfSkinfold);
      if (newMeasurement.notes) measurementData.notes = newMeasurement.notes.trim();

      const measurementRef = push(ref(database, `users/${selectedPatient}/measurements`));
      await set(measurementRef, measurementData);

      setIsModalOpen(false);
      setNewMeasurement({
        weight: '',
        height: '',
        bodyFatPercentage: '',
        muscleMass: '',
        waistCircumference: '',
        hipCircumference: '',
        armCircumference: '',
        thighCircumference: '',
        tricepsSkinfold: '',
        bicepsSkinfold: '',
        subscapularSkinfold: '',
        suprailiacSkinfold: '',
        abdominalSkinfold: '',
        thighSkinfold: '',
        calfSkinfold: '',
        notes: '',
      });

      await loadMeasurements(selectedPatient);
      window.alert('Medição salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar medição:', error);
      window.alert('Erro ao salvar medição');
    } finally {
      setSaving(false);
    }
  };

  const getPatientName = (id: string) => {
    const p = pacientes.find((x) => x.id === id);
    return p?.nome || 'Paciente';
  };

  const formatDate = (iso: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const calculateBMI = (weight?: number, height?: number) => {
    if (!weight || !height || height === 0) return null;
    const heightInMeters = height / 100;
    return (weight / (heightInMeters * heightInMeters)).toFixed(1);
  };

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Medições Antropométricas</h2>
          <p>Registre e acompanhe as medições corporais dos seus pacientes.</p>
        </div>
        <div className="ns-page-header-actions">
          {selectedPatient && measurements.length > 0 && (
            <>
              <Button
                variant="secondary"
                onClick={() => exportMeasurementsToPDF(measurements, getPatientName(selectedPatient))}
                style={{ marginRight: '8px' }}
              >
                📄 Exportar PDF
              </Button>
              <Button
                variant="secondary"
                onClick={async () => {
                  if (!user?.uid || !selectedPatient) return;
                  try {
                    const jsPDF = (await import('jspdf')).default;
                    const autoTable = (await import('jspdf-autotable')).default;

                    const doc = new jsPDF();
                    doc.setFontSize(18);
                    doc.text('Relatório de Medições Antropométricas', 14, 20);
                    doc.setFontSize(12);
                    doc.text(`Paciente: ${getPatientName(selectedPatient)}`, 14, 28);
                    doc.text(`Data: ${new Date().toLocaleDateString('pt-BR')}`, 14, 34);

                    const tableData = measurements.map(m => [
                      new Date(m.timestamp).toLocaleDateString('pt-BR'),
                      m.weight ? `${m.weight.toFixed(1)} kg` : '-',
                      m.height ? `${m.height.toFixed(1)} cm` : '-',
                      m.bodyFatPercentage ? `${m.bodyFatPercentage.toFixed(1)}%` : '-',
                    ]);

                    autoTable(doc, {
                      startY: 40,
                      head: [['Data', 'Peso', 'Altura', '% Gordura']],
                      body: tableData,
                    });

                    const blob = await generatePDFBlob(doc);
                    setPdfBlob(blob);
                    setIsPDFSendModalOpen(true);
                  } catch (error) {
                    console.error('Erro ao gerar PDF:', error);
                    alert('Erro ao gerar PDF. Tente novamente.');
                  }
                }}
                style={{ marginRight: '8px' }}
              >
                📤 Enviar PDF para Paciente
              </Button>
            </>
          )}
          {selectedPatient && (
            <Button onClick={() => setIsModalOpen(true)}>Nova Medição</Button>
          )}
        </div>
      </div>

      <Card title="Selecionar Paciente" subtitle="Escolha um paciente para visualizar suas medições">
        <label className="ns-field">
          <span>Paciente</span>
          <select
            className="ns-input"
            value={selectedPatient}
            onChange={(e) => setSelectedPatient(e.target.value)}
          >
            <option value="">Selecione um paciente</option>
            {pacientes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
          </select>
        </label>
      </Card>

      {selectedPatient && (
        <>
          {loading ? (
            <Card>
              <p>Carregando medições...</p>
            </Card>
          ) : measurements.length === 0 ? (
            <Card>
              <p>Nenhuma medição registrada para este paciente ainda.</p>
              <Button onClick={() => setIsModalOpen(true)} style={{ marginTop: '12px' }}>
                Registrar Primeira Medição
              </Button>
            </Card>
          ) : (
            <>
              {/* Gráficos de Evolução */}
              <div className="ns-grid ns-grid-2" style={{ marginBottom: '20px' }}>
                <Card title="Evolução do Peso" subtitle="Histórico de peso ao longo do tempo">
                  {measurements.filter(m => m.weight).length > 0 ? (
                    <div style={{ height: '200px', position: 'relative' }}>
                      <svg width="100%" height="200" style={{ overflow: 'visible' }}>
                        <defs>
                          <linearGradient id="weightGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
                            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {(() => {
                          const weightData = measurements
                            .filter(m => m.weight)
                            .slice(0, 10)
                            .reverse();
                          if (weightData.length < 2) return null;

                          const maxWeight = Math.max(...weightData.map(m => m.weight!));
                          const minWeight = Math.min(...weightData.map(m => m.weight!));
                          const range = maxWeight - minWeight || 1;
                          const width = 100;
                          const height = 180;
                          const padding = 20;
                          const chartWidth = width - (padding * 2);
                          const chartHeight = height - (padding * 2);

                          const points = weightData.map((m, i) => {
                            const x = padding + (i / (weightData.length - 1)) * chartWidth;
                            const y = padding + chartHeight - ((m.weight! - minWeight) / range) * chartHeight;
                            return `${x},${y}`;
                          }).join(' ');

                          const areaPoints = `${points} ${padding + chartWidth},${padding + chartHeight} ${padding},${padding + chartHeight}`;

                          return (
                            <>
                              <polyline
                                points={areaPoints}
                                fill="url(#weightGradient)"
                                stroke="none"
                              />
                              <polyline
                                points={points}
                                fill="none"
                                stroke="#0ea5e9"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              {weightData.map((m, i) => {
                                const x = padding + (i / (weightData.length - 1)) * chartWidth;
                                const y = padding + chartHeight - ((m.weight! - minWeight) / range) * chartHeight;
                                return (
                                  <circle
                                    key={i}
                                    cx={x}
                                    cy={y}
                                    r="4"
                                    fill="#0ea5e9"
                                    stroke="#fff"
                                    strokeWidth="2"
                                  />
                                );
                              })}
                            </>
                          );
                        })()}
                      </svg>
                    </div>
                  ) : (
                    <p className="ns-chart-empty">
                      Dados insuficientes para exibir gráfico
                    </p>
                  )}
                </Card>

                <Card title="Evolução do IMC" subtitle="Índice de Massa Corporal">
                  {measurements.filter(m => m.weight && m.height).length > 0 ? (
                    <div style={{ height: '200px', position: 'relative' }}>
                      <svg width="100%" height="200" style={{ overflow: 'visible' }}>
                        {(() => {
                          const bmiData = measurements
                            .filter(m => m.weight && m.height)
                            .map(m => ({ ...m, bmi: calculateBMI(m.weight, m.height) }))
                            .filter(m => m.bmi)
                            .slice(0, 10)
                            .reverse();
                          if (bmiData.length < 2) return null;

                          const maxBMI = Math.max(...bmiData.map(m => parseFloat(m.bmi!)));
                          const minBMI = Math.min(...bmiData.map(m => parseFloat(m.bmi!)));
                          const range = maxBMI - minBMI || 1;
                          const width = 100;
                          const height = 180;
                          const padding = 20;
                          const chartWidth = width - (padding * 2);
                          const chartHeight = height - (padding * 2);

                          const points = bmiData.map((m, i) => {
                            const x = padding + (i / (bmiData.length - 1)) * chartWidth;
                            const y = padding + chartHeight - ((parseFloat(m.bmi!) - minBMI) / range) * chartHeight;
                            return `${x},${y}`;
                          }).join(' ');

                          return (
                            <polyline
                              points={points}
                              fill="none"
                              stroke="#10b981"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          );
                        })()}
                      </svg>
                    </div>
                  ) : (
                    <p className="ns-chart-empty">
                      Dados insuficientes para exibir gráfico
                    </p>
                  )}
                </Card>
              </div>

              {/* Gráficos Comparativos Avançados */}
              <div style={{ marginBottom: '20px' }}>
                <Card
                  title="Gráficos Comparativos Avançados"
                  subtitle="Evolução de bioimpedância e dobras cutâneas"
                >
                  <AdvancedCharts measurements={measurements} />
                  <div className="ns-divider">
                    <h4 className="ns-section-title ns-section-title-tight">Dados Detalhados</h4>
                    <DetailedMeasurementsTable measurements={measurements} />
                  </div>
                </Card>
              </div>

              {/* Painel Comparativo abaixo dos gráficos */}
              {measurements.length >= 2 && (
                <div style={{ marginBottom: '20px' }}>
                  <Card
                    title="Comparação de Evolução"
                    subtitle="Comparação entre a última e a penúltima medição"
                  >
                    <ComparisonPanel current={measurements[0]} previous={measurements[1]} />
                  </Card>
                </div>
              )}

              {/* Painel de Histórico de Edições */}
              <div style={{ marginBottom: '20px' }}>
                <Card
                  title="Histórico de Edições"
                  subtitle="Últimas 5 medições registradas"
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {measurements.slice(0, 5).map((m, idx) => {
                      const prevMeasurement = measurements[idx + 1];
                      const bmi = calculateBMI(m.weight, m.height);

                      // Calcular diferenças para múltiplos campos
                      const calculateDiff = (current?: number, previous?: number): { value: number; percent: number; trend: 'up' | 'down' | 'stable' } | null => {
                        if (!current || current === null || !previous || previous === null) return null;
                        const diff = current - previous;
                        const percent = previous !== 0 ? (diff / previous) * 100 : 0;
                        return { value: diff, percent, trend: Math.abs(percent) < 0.1 ? 'stable' as const : (diff > 0 ? 'up' as const : 'down' as const) };
                      };

                      const weightDiff = calculateDiff(m.weight, prevMeasurement?.weight);
                      const bodyFatDiff = calculateDiff(m.bodyFatPercentage, prevMeasurement?.bodyFatPercentage);
                      const muscleDiff = calculateDiff(m.muscleMass, prevMeasurement?.muscleMass);
                      const waistDiff = calculateDiff(m.waistCircumference, prevMeasurement?.waistCircumference);

                      const getTrendColor = (trend: 'up' | 'down' | 'stable' | null, isNegativeGood: boolean = false) => {
                        if (!trend) return '#64748b';
                        if (trend === 'stable') return '#64748b';
                        if (isNegativeGood) {
                          return trend === 'down' ? '#10b981' : '#ef4444';
                        } else {
                          return trend === 'up' ? '#10b981' : '#ef4444';
                        }
                      };

                      const getTrendIcon = (trend: 'up' | 'down' | 'stable' | null, isNegativeGood: boolean = false) => {
                        if (!trend || trend === 'stable') return '➡️';
                        if (isNegativeGood) {
                          return trend === 'down' ? '📉' : '📈';
                        } else {
                          return trend === 'up' ? '📈' : '📉';
                        }
                      };

                      return (
                        <div
                          key={m.id}
                          className="ns-meas-card"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div>
                              <strong className="ns-meas-date">
                                {formatDate(m.timestamp)}
                              </strong>
                              {prevMeasurement && (
                                <span className="ns-mini-label">
                                  Comparado com {formatDate(prevMeasurement.timestamp)}
                                </span>
                              )}
                            </div>
                            {weightDiff && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '18px' }}>
                                  {getTrendIcon(weightDiff.trend, true)}
                                </span>
                                <span
                                  style={{
                                    backgroundColor: getTrendColor(weightDiff.trend, true) === '#10b981' ? '#d1fae5' : getTrendColor(weightDiff.trend, true) === '#ef4444' ? '#fee2e2' : '#f3f4f6',
                                    color: getTrendColor(weightDiff.trend, true),
                                    padding: '4px 10px',
                                    borderRadius: '12px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                  }}
                                >
                                  {weightDiff.value > 0 ? '+' : ''}{weightDiff.value.toFixed(1)} kg ({weightDiff.percent > 0 ? '+' : ''}{weightDiff.percent.toFixed(1)}%)
                                </span>
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', fontSize: '13px' }}>
                            {m.weight && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">Peso</div>
                                <div className="ns-mini-value">{m.weight.toFixed(1)} kg</div>
                                {weightDiff && (
                                  <div style={{ fontSize: '10px', color: getTrendColor(weightDiff.trend, true), marginTop: '2px' }}>
                                    {getTrendIcon(weightDiff.trend, true)} {weightDiff.value > 0 ? '+' : ''}{weightDiff.value.toFixed(1)} kg
                                  </div>
                                )}
                              </div>
                            )}
                            {bmi && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">IMC</div>
                                <div className="ns-mini-value">{bmi}</div>
                              </div>
                            )}
                            {m.bodyFatPercentage && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">% Gordura</div>
                                <div className="ns-mini-value">{m.bodyFatPercentage.toFixed(1)}%</div>
                                {bodyFatDiff && (
                                  <div style={{ fontSize: '10px', color: getTrendColor(bodyFatDiff.trend, true), marginTop: '2px' }}>
                                    {getTrendIcon(bodyFatDiff.trend, true)} {bodyFatDiff.value > 0 ? '+' : ''}{bodyFatDiff.value.toFixed(1)}%
                                  </div>
                                )}
                              </div>
                            )}
                            {m.muscleMass && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">Massa Muscular</div>
                                <div className="ns-mini-value">{m.muscleMass.toFixed(1)} kg</div>
                                {muscleDiff && (
                                  <div style={{ fontSize: '10px', color: getTrendColor(muscleDiff.trend, false), marginTop: '2px' }}>
                                    {getTrendIcon(muscleDiff.trend, false)} {muscleDiff.value > 0 ? '+' : ''}{muscleDiff.value.toFixed(1)} kg
                                  </div>
                                )}
                              </div>
                            )}
                            {m.waistCircumference && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">Cintura</div>
                                <div className="ns-mini-value">{m.waistCircumference.toFixed(1)} cm</div>
                                {waistDiff && (
                                  <div style={{ fontSize: '10px', color: getTrendColor(waistDiff.trend, true), marginTop: '2px' }}>
                                    {getTrendIcon(waistDiff.trend, true)} {waistDiff.value > 0 ? '+' : ''}{waistDiff.value.toFixed(1)} cm
                                  </div>
                                )}
                              </div>
                            )}
                            {m.hipCircumference && (
                              <div className="ns-mini-stat">
                                <div className="ns-mini-label">Quadril</div>
                                <div className="ns-mini-value">{m.hipCircumference.toFixed(1)} cm</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              <Card
                title={`Medições de ${getPatientName(selectedPatient)}`}
                subtitle={`${measurements.length} medição(ões) registrada(s)`}
              >
                <div className="ns-table-wrapper">
                  <table className="ns-table">
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Peso (kg)</th>
                        <th>Altura (cm)</th>
                        <th>IMC</th>
                        <th>% Gordura</th>
                        <th>Massa Muscular (kg)</th>
                        <th>Cintura (cm)</th>
                        <th>Quadril (cm)</th>
                        <th>Braço (cm)</th>
                        <th>Coxa (cm)</th>
                        <th>Dobras Cutâneas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {measurements.map((m) => {
                        const bmi = calculateBMI(m.weight, m.height);
                        return (
                          <tr key={m.id}>
                            <td>{formatDate(m.timestamp)}</td>
                            <td>{m.weight ? `${m.weight} kg` : '-'}</td>
                            <td>{m.height ? `${m.height} cm` : '-'}</td>
                            <td>{bmi ? bmi : '-'}</td>
                            <td>{m.bodyFatPercentage ? `${m.bodyFatPercentage}%` : '-'}</td>
                            <td>{m.muscleMass ? `${m.muscleMass} kg` : '-'}</td>
                            <td>{m.waistCircumference ? `${m.waistCircumference} cm` : '-'}</td>
                            <td>{m.hipCircumference ? `${m.hipCircumference} cm` : '-'}</td>
                            <td>{m.armCircumference ? `${m.armCircumference} cm` : '-'}</td>
                            <td>{m.thighCircumference ? `${m.thighCircumference} cm` : '-'}</td>
                            <td>
                              {m.tricepsSkinfold || m.bicepsSkinfold || m.subscapularSkinfold || m.suprailiacSkinfold || m.abdominalSkinfold || m.thighSkinfold || m.calfSkinfold ? (
                                <div style={{ fontSize: '11px', lineHeight: '1.4' }}>
                                  {m.tricepsSkinfold && <div>Tríceps: {m.tricepsSkinfold}mm</div>}
                                  {m.bicepsSkinfold && <div>Bíceps: {m.bicepsSkinfold}mm</div>}
                                  {m.subscapularSkinfold && <div>Subescapular: {m.subscapularSkinfold}mm</div>}
                                  {m.suprailiacSkinfold && <div>Suprailíaca: {m.suprailiacSkinfold}mm</div>}
                                  {m.abdominalSkinfold && <div>Abdominal: {m.abdominalSkinfold}mm</div>}
                                  {m.thighSkinfold && <div>Coxa: {m.thighSkinfold}mm</div>}
                                  {m.calfSkinfold && <div>Panturrilha: {m.calfSkinfold}mm</div>}
                                </div>
                              ) : '-'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`Nova Medição - ${selectedPatient ? getPatientName(selectedPatient) : 'Paciente'}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar Medição'}
            </Button>
          </>
        }
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0 4px' }}>
          <form className="ns-form" onSubmit={(e) => e.preventDefault()}>
            <h3 className="ns-section-title ns-section-title-first">
              Medidas Básicas
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Peso (kg)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.weight}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, weight: e.target.value })}
                  placeholder="Ex: 70.5"
                />
              </label>
              <label className="ns-field">
                <span>Altura (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.height}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, height: e.target.value })}
                  placeholder="Ex: 170"
                />
              </label>
            </div>

            <h3 className="ns-section-title">
              Composição Corporal
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>% Gordura Corporal</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.bodyFatPercentage}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, bodyFatPercentage: e.target.value })}
                  placeholder="Ex: 20.5"
                />
              </label>
              <label className="ns-field">
                <span>Massa Muscular (kg)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.muscleMass}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, muscleMass: e.target.value })}
                  placeholder="Ex: 50.0"
                />
              </label>
            </div>

            <h3 className="ns-section-title">
              Circunferências
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Cintura (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.waistCircumference}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, waistCircumference: e.target.value })}
                  placeholder="Ex: 85.0"
                />
              </label>
              <label className="ns-field">
                <span>Quadril (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.hipCircumference}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, hipCircumference: e.target.value })}
                  placeholder="Ex: 95.0"
                />
              </label>
              <label className="ns-field">
                <span>Braço (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.armCircumference}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, armCircumference: e.target.value })}
                  placeholder="Ex: 30.0"
                />
              </label>
              <label className="ns-field">
                <span>Coxa (cm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.thighCircumference}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, thighCircumference: e.target.value })}
                  placeholder="Ex: 55.0"
                />
              </label>
            </div>

            <h3 className="ns-section-title">
              Dobras Cutâneas (mm)
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label className="ns-field">
                <span>Tríceps (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.tricepsSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, tricepsSkinfold: e.target.value })}
                  placeholder="Ex: 12.5"
                />
              </label>
              <label className="ns-field">
                <span>Bíceps (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.bicepsSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, bicepsSkinfold: e.target.value })}
                  placeholder="Ex: 8.0"
                />
              </label>
              <label className="ns-field">
                <span>Subescapular (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.subscapularSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, subscapularSkinfold: e.target.value })}
                  placeholder="Ex: 15.0"
                />
              </label>
              <label className="ns-field">
                <span>Suprailíaca (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.suprailiacSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, suprailiacSkinfold: e.target.value })}
                  placeholder="Ex: 18.0"
                />
              </label>
              <label className="ns-field">
                <span>Abdominal (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.abdominalSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, abdominalSkinfold: e.target.value })}
                  placeholder="Ex: 20.0"
                />
              </label>
              <label className="ns-field">
                <span>Coxa (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.thighSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, thighSkinfold: e.target.value })}
                  placeholder="Ex: 25.0"
                />
              </label>
              <label className="ns-field">
                <span>Panturrilha (mm)</span>
                <input
                  className="ns-input"
                  type="number"
                  step="0.1"
                  value={newMeasurement.calfSkinfold}
                  onChange={(e) => setNewMeasurement({ ...newMeasurement, calfSkinfold: e.target.value })}
                  placeholder="Ex: 10.0"
                />
              </label>
            </div>

            <h3 className="ns-section-title">
              Observações
            </h3>
            <label className="ns-field">
              <span>Observações sobre a medição</span>
              <textarea
                className="ns-textarea"
                value={newMeasurement.notes}
                onChange={(e) => setNewMeasurement({ ...newMeasurement, notes: e.target.value })}
                placeholder="Anote observações importantes sobre esta medição..."
                rows={4}
              />
            </label>
          </form>
        </div>
      </Modal>

      <PDFSendModal
        isOpen={isPDFSendModalOpen}
        onClose={() => {
          setIsPDFSendModalOpen(false);
          setPdfBlob(null);
        }}
        onSend={async (message) => {
          if (!user?.uid || !selectedPatient || !pdfBlob) return;
          try {
            const fileName = `medicoes_${getPatientName(selectedPatient).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
            await sendPDFToPatient(selectedPatient, user.uid, 'measurements', fileName, pdfBlob, message);
            alert('PDF enviado para o paciente com sucesso!');
          } catch (error) {
            console.error('Erro ao enviar PDF:', error);
            alert('Erro ao enviar PDF. Tente novamente.');
            throw error;
          }
        }}
        pdfBlob={pdfBlob}
        fileName={pdfBlob ? `medicoes_${getPatientName(selectedPatient).replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf` : ''}
        patientName={getPatientName(selectedPatient)}
      />
    </div>
  );
};

